/**
 * 可选外键 Id：0 / 空 转为 null，避免 el-select 显示裸 0
 */
export function normalizeOptionalId(id) {
  const n = Number(id);
  return n > 0 ? n : null;
}

/** 审批流校区下拉：全校占位值（el-select 无法正确匹配 value=0） */
export const ALL_CAMPUS_FORM_VALUE = '__all_campus__';

/**
 * 审批流 GradeId 回显：0 / -1 / 空 → 全校占位值
 */
export function normalizeGradeIdForForm(gradeId) {
  const n = Number(gradeId);
  return n > 0 ? n : ALL_CAMPUS_FORM_VALUE;
}

/**
 * 审批流 GradeId 提交：占位值 → 0
 */
export function gradeIdToPayload(formValue) {
  if (formValue === ALL_CAMPUS_FORM_VALUE || formValue == null || formValue === '') return 0;
  const n = Number(formValue);
  return n > 0 ? n : 0;
}

/**
 * 审批流 GradeId 转数字筛选（0=全校）
 */
export function gradeIdToFilterNumber(formValue) {
  if (formValue === ALL_CAMPUS_FORM_VALUE) return 0;
  const n = Number(formValue);
  return n > 0 ? n : 0;
}

/**
 * 雪花 Id 等长整型：统一转字符串，避免 el-select 匹配失败
 */
export function normalizeOptionalIdString(id) {
  if (id == null || id === '') return null;
  const text = String(id).trim();
  if (!text || text === '0') return null;
  return text;
}

/**
 * 提交可选外键：null / 空 转为 0
 */
export function toOptionalIdPayload(id) {
  return Number(id) || 0;
}

/**
 * 解析会议预约时间字符串
 */
export function parseMeetingDateTime(value) {
  if (!value) return null;
  const date = new Date(String(value).replace(/-/g, '/'));
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * 日期选择器：禁用今天之前的日期
 */
export const meetingBookingTimePickerOptions = {
  disabledDate(time) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return time.getTime() < today.getTime();
  }
};

/**
 * 开始时间校验：不能早于当前时间
 */
export function createMeetingStartTimeRule() {
  return {
    validator(rule, value, callback) {
      if (!value) return callback();
      const start = parseMeetingDateTime(value);
      if (!start) return callback(new Error('开始时间格式无效'));
      if (start.getTime() < Date.now()) {
        return callback(new Error('开始时间不能早于当前时间'));
      }
      callback();
    },
    trigger: 'change'
  };
}

/**
 * 结束时间校验：须晚于开始时间，且不能早于当前时间
 */
export function createMeetingEndTimeRule(getStartTime) {
  return {
    validator(rule, value, callback) {
      if (!value) return callback();
      const end = parseMeetingDateTime(value);
      if (!end) return callback(new Error('结束时间格式无效'));
      const startStr = typeof getStartTime === 'function' ? getStartTime() : '';
      if (startStr) {
        const start = parseMeetingDateTime(startStr);
        if (start && end.getTime() <= start.getTime()) {
          return callback(new Error('结束时间必须晚于开始时间'));
        }
      }
      if (end.getTime() < Date.now()) {
        return callback(new Error('结束时间不能早于当前时间'));
      }
      callback();
    },
    trigger: 'change'
  };
}

/**
 * 格式化为 yyyy-MM-dd HH:mm:ss
 */
export function formatMeetingDateTime(date) {
  if (!date || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${d} ${hh}:${mm}:${ss}`;
}

/**
 * 用 start 的日期 + end 的时分秒，拼成同一天的结束时间（周期会议用）
 */
export function combineMeetingDateWithTimeOfDay(startValue, endValue) {
  const start = parseMeetingDateTime(startValue);
  const end = parseMeetingDateTime(endValue);
  if (!start || !end) return endValue || '';
  const merged = new Date(start.getTime());
  merged.setHours(end.getHours(), end.getMinutes(), end.getSeconds(), 0);
  return formatMeetingDateTime(merged);
}

/**
 * 周期会议结束时间：须与开始同一天，且晚于开始
 */
export function createMeetingSameDayEndTimeRule(getStartTime) {
  return {
    validator(rule, value, callback) {
      if (!value) return callback();
      const end = parseMeetingDateTime(value);
      if (!end) return callback(new Error('结束时间格式无效'));
      const startStr = typeof getStartTime === 'function' ? getStartTime() : '';
      if (!startStr) return callback();
      const start = parseMeetingDateTime(startStr);
      if (!start) return callback();
      if (
        start.getFullYear() !== end.getFullYear()
        || start.getMonth() !== end.getMonth()
        || start.getDate() !== end.getDate()
      ) {
        return callback(new Error('周期会议须在同一天内（开始与结束不能跨天）'));
      }
      if (end.getTime() <= start.getTime()) {
        return callback(new Error('结束时间必须晚于开始时间'));
      }
      if (end.getTime() < Date.now()) {
        return callback(new Error('结束时间不能早于当前时间'));
      }
      callback();
    },
    trigger: 'change'
  };
}

/**
 * 周期会议结束日期选择：只能选开始那天
 */
export function createSameDayEndPickerOptions(getStartTime) {
  return {
    disabledDate(time) {
      const startStr = typeof getStartTime === 'function' ? getStartTime() : '';
      const start = parseMeetingDateTime(startStr);
      if (!start) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return time.getTime() < today.getTime();
      }
      const day = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
      const t = new Date(time.getFullYear(), time.getMonth(), time.getDate()).getTime();
      return t !== day;
    }
  };
}
